import { Fragment, useRef } from "react";

import clsx from "clsx";

import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";

import FieldLabel from "../Field-Label";

import { SingleSelectProps } from "./types";

function SingleSelect<T extends number | string>({
  label,
  labelTags,
  name,
  selected,
  onChange,
  help,
  error,
  required,
  placeholder,
  ...rest
}: SingleSelectProps<T>) {
  const node = useRef();
  const selectedDisp =
    "options" in rest
      ? rest.options.find((opt) => opt.value === selected)?.label
      : selected;

  return (
    <div ref={node}>
      <Listbox value={selected} onChange={onChange}>
        {({ open }) => (
          <>
            {label && (
              <Listbox.Label>
                <FieldLabel
                  name={name}
                  label={label}
                  error={!!error}
                  required={required}
                  tags={labelTags}
                />
              </Listbox.Label>
            )}
            <div className="relative mt-1">
              <Listbox.Button
                className={clsx(
                  "relative w-full",
                  "py-2 pl-3 pr-10 text-left",
                  "bg-white cursor-default",
                  "border-b-2",
                  "border-gray-300",
                  "hover:border-gray-400",
                  "focus:border-primary-main",
                  "focus:text-primary-main",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500",
                  "sm:text-sm md:text-base"
                )}
              >
                <span
                  className={clsx(
                    "block truncate",
                    !selectedDisp && "text-gray-500"
                  )}
                >
                  {selectedDisp || placeholder}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                  {selected !== null && selected !== undefined && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-label="clear selection"
                      className="h-4 w-4 text-gray-600 cursor-pointer z-10"
                      onClick={() => onChange(null)}
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  <SelectorIcon
                    className="w-5 h-5 text-gray-400 pointer-events-none"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                show={open}
              >
                <Listbox.Options className="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm md:text-base z-50">
                  {"options" in rest
                    ? rest.options.map((opt) => (
                        <Listbox.Option
                          key={opt.value}
                          className={({ active }) =>
                            clsx(
                              active
                                ? "text-blue-900 bg-blue-100"
                                : "text-gray-900",
                              "cursor-default select-none relative py-2 pl-10 pr-4"
                            )
                          }
                          value={opt.value}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={clsx(
                                  selected ? "font-medium" : "font-normal",
                                  "block truncate"
                                )}
                              >
                                {opt.displayStr || opt.label}
                              </span>
                              {selected && (
                                <span
                                  className={
                                    "text-blue-600 absolute inset-y-0 left-0 flex items-center pl-3"
                                  }
                                >
                                  <CheckIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))
                    : rest.allowedValues.map((value) => (
                        <Listbox.Option
                          key={value}
                          className={({ active }) =>
                            `${
                              active
                                ? "text-blue-900 bg-blue-100"
                                : "text-gray-900"
                            }
                          cursor-default select-none relative py-2 pl-10 pr-4`
                          }
                          value={value}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={clsx(
                                  selected ? "font-medium" : "font-normal",
                                  "block truncate"
                                )}
                              >
                                {value}
                              </span>
                              {selected && (
                                <span
                                  className={
                                    "text-blue-600 absolute inset-y-0 left-0 flex items-center pl-3"
                                  }
                                >
                                  <CheckIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
      {help && (
        <div
          className={clsx(
            "text-sm",
            "pt-1",
            !!error ? "text-error-dark" : "text-gray-700"
          )}
        >
          {help}
        </div>
      )}
      {error && <div className="text-error-dark text-sm pt-1">{error}</div>}
    </div>
  );
}

export default SingleSelect;
